import { useEffect, useState, type FC } from "react";
import { Modal, ModalBody, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import type { ProblemResponse } from "@/lib/http";
import type { SubscriptionModel } from "@/features/common/api/useSubscription";
import { useChangePaymentCard } from "@/features/subscriptions/api/useChangePaymentCard";
import { PaymentCardPicker } from "@/features/subscriptions/components/PaymentCardPicker";

export interface ModalChangePaymentCardProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	subscription: SubscriptionModel;
}

export const ModalChangePaymentCard: FC<ModalChangePaymentCardProps> = props => {
	const {
		open,
		onOpenChange,
		subscription
	} = props;

	const changePaymentCard = useChangePaymentCard();
	useNotifyMutationSuccess(changePaymentCard, "Payment card updated");

	useEffect(() => {
		if (open) {
			changePaymentCard.reset();
		}
	}, [open]);

	useEffect(() => {
		if (changePaymentCard.isSuccess) {
			onOpenChange(false);
		}
	}, [changePaymentCard.isSuccess]);

	return (
		<Modal open={open} onOpenChange={onOpenChange} preventCloseOnInteractOutside={changePaymentCard.isPending}>
			<ModalHeader>
				<ModalTitle>Change Payment Card</ModalTitle>
				<ModalDescription>Choose the card to charge when your subscription renews.</ModalDescription>
			</ModalHeader>

			<ModalChangePaymentCardContent
				currentPaymentCardId={subscription.autoRenewPaymentCardId}
				isPending={changePaymentCard.isPending}
				error={changePaymentCard.error}
				onCancel={() => onOpenChange(false)}
				onSubmit={paymentCardId => changePaymentCard.mutate({ paymentCardId })} />
		</Modal>
	);
};

interface ModalChangePaymentCardContentProps {
	currentPaymentCardId: string | null;
	isPending: boolean;
	error: ProblemResponse | null;
	onCancel: () => void;
	onSubmit: (paymentCardId: string) => void;
}

const ModalChangePaymentCardContent: FC<ModalChangePaymentCardContentProps> = props => {
	const {
		currentPaymentCardId,
		isPending,
		error,
		onCancel,
		onSubmit
	} = props;

	const [paymentCardId, setPaymentCardId] = useState<string | null>(currentPaymentCardId);
	const canSubmit = !!paymentCardId && paymentCardId !== currentPaymentCardId && !isPending;

	return (
		<>
			<ModalBody>
				<div className="flex flex-col gap-3">
					<PaymentCardPicker value={paymentCardId} onChange={setPaymentCardId} disabled={isPending} />
					<ErrorDisplay error={error} />
				</div>
			</ModalBody>

			<ModalFooter>
				<Button variant="outline" disabled={isPending} onClick={onCancel}>
					Cancel
				</Button>
				<Button disabled={!canSubmit} onClick={() => paymentCardId && onSubmit(paymentCardId)}>
					{isPending ? <Spinner /> : "Update Card"}
				</Button>
			</ModalFooter>
		</>
	);
};
